import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api.js'

/**
 * Mapa slug → label legível.
 * Usado para restaurar o label após refresh de página (o servidor não conhece o label).
 */
const COMMAND_LABELS = {
  restart:              'Reiniciar',
  exchange_money:       'Carregamento Notas',
  exchange_coins:       'Carregamento Moedas',
  replenishment_end:    'Concluir Carregamento',
  replenishment_cancel: 'Cancelar Carregamento',
  unload_money:         'Descarga Notas',
  unload_coins:         'Descarga Moedas',
  change_money:         'Destrocar',
  change_money_cancel:  'Cancelar Troca',
  remove_safe_notes:    'Retirar Cofre Notas',
  remove_safe_coins:    'Retirar Cofre Moedas',
  set_cash_fund:        'Fundo de Caixa',
  close_cash_fund:      'Fechar Caixa',
  withdraw_cash_fund:   'Retirar Fundo',
  edit_cash_fund:       'Alterar Fundo',
  cancel_current:       'Cancelar Operação',
  change_cancel:        'Cancelar Pagamento',
  charge_cancel:        'Cancelar Cobrança',
  cashin_cancel:        'Cancelar Entrada',
  change_money_cancel:  'Cancelar Destrocar',
}

/** Quanto tempo (ms) o card de sucesso fica visível antes de fechar automaticamente */
const SUCCESS_AUTO_CLOSE_MS = 30_000

/**
 * Store de fila de comandos do terminal.
 *
 * Estados de um comando rastreado:
 *  - Pendente    is_processed=false, has_error=false, _succeeded=false  → spinner
 *  - A executar  is_processing=false, is_executing=true                 → spinner diferente
 *  - Sucesso     is_processed=true,  has_error=false, _succeeded=true   → card verde (30s auto-close)
 *  - Erro        is_processed=true,  has_error=true                     → card vermelho (close manual)
 *
 * Fluxo:
 *  1. enqueue()       → cria no servidor, adiciona ao estado local, arranca polling
 *  2. _poll()         → a cada 5s sincroniza is_executing + deteta conclusão
 *  3. Saiu da pending → _fetchStatus() para determinar sucesso ou erro
 *  4. Sucesso         → marca _succeeded=true, inicia timer 30s de auto-close
 *  5. Erro            → actualiza em-lugar, utilizador fecha manualmente
 *  6. dismissCommand  → limpa timer (se existir) e remove do estado
 *
 * Após refresh de página:
 *  initialize() consulta /commands/pending, restaura estado e retoma polling.
 */
export const useCommandQueueStore = defineStore('commandQueue', () => {

  // ---------------------------------------------------------------------------
  // Estado
  // ---------------------------------------------------------------------------

  /**
   * Comandos a monitorizar.
   * Campos extra (só no frontend): label, _succeeded
   */
  const trackedCommands = ref([])

  /**
   * Slugs cujo pedido ao servidor ainda está em trânsito.
   * Bloqueia o botão imediatamente no click, antes de a API responder.
   */
  const _submittingSlugs = ref([])

  /** ID do setInterval de polling (null = parado) */
  const _pollingTimer = ref(null)

  /** Terminal activo */
  const _terminalId = ref(null)

  /**
   * Timers de auto-close para comandos com sucesso.
   * Plain Map — não precisa de ser reactivo (uso interno).
   * commandId → timeoutId
   */
  const _autoCloseTimers = new Map()

  // ---------------------------------------------------------------------------
  // Computed
  // ---------------------------------------------------------------------------

  /**
   * Comandos visíveis no widget:
   *  - pendentes/a executar  (is_processed=false)
   *  - com erro              (is_processed=true, has_error=true)
   *  - com sucesso recente   (is_processed=true, _succeeded=true) — até auto-close
   */
  const activeCommands = computed(() =>
    trackedCommands.value.filter(c => !c.is_processed || c.has_error || c._succeeded)
  )

  /** True se houver pelo menos um comando ainda a aguardar processamento */
  const hasPending = computed(() =>
    trackedCommands.value.some(c => !c.is_processed)
  )

  // ---------------------------------------------------------------------------
  // API pública
  // ---------------------------------------------------------------------------

  /**
   * Indica se um slug está bloqueado (em trânsito OU pendente/com erro).
   * Comandos com sucesso NÃO bloqueiam — o utilizador pode re-enviar.
   */
  function isCommandLocked(slug) {
    return _submittingSlugs.value.includes(slug)
      || trackedCommands.value.some(c => c.command === slug && !c.is_processed && !c.has_error)
      || trackedCommands.value.some(c => c.command === slug && c.has_error)
  }

  /**
   * Inicializa para um terminal: consulta pendentes e retoma polling se necessário.
   * Chamar no boot e quando o terminal muda.
   */
  async function initialize(terminalId) {
    _terminalId.value = terminalId

    const pending = await _fetchPending(terminalId)

    if (pending.length > 0) {
      pending.forEach(cmd => {
        if (!trackedCommands.value.find(c => c.id === cmd.id)) {
          trackedCommands.value.push({
            ...cmd,
            label:     COMMAND_LABELS[cmd.command] ?? cmd.command,
            _succeeded: false,
          })
        }
      })
      _startPolling(terminalId)
    }
  }

  /**
   * Enfileira um comando no servidor e começa a monitorizar.
   *
   * @param {number}      terminalId   - ID do terminal
   * @param {string}      commandSlug  - Slug = slug de permissão (ex: 'restart')
   * @param {string}      label        - Label legível para o widget
   * @param {object|null} payload      - Payload opcional
   * @param {string|null} observations - Observações opcionais (obrigatórias em alguns comandos)
   */
  async function enqueue(terminalId, commandSlug, label, payload = null, observations = null) {
    // Bloqueia o botão antes da API responder
    _submittingSlugs.value = [..._submittingSlugs.value, commandSlug]

    let data
    try {
      ;({ data } = await api.post(`/terminals/${terminalId}/commands`, {
        command: commandSlug,
        payload,
        observations,
      }))
    } finally {
      _submittingSlugs.value = _submittingSlugs.value.filter(s => s !== commandSlug)
    }

    if (!data.success) {
      throw new Error(data.error ?? 'Erro ao enfileirar comando')
    }

    const cmd = { ...data.command, label, _succeeded: false }

    const existingIdx = trackedCommands.value.findIndex(c => c.id === cmd.id)
    if (existingIdx >= 0) {
      trackedCommands.value[existingIdx] = cmd
    } else {
      trackedCommands.value.push(cmd)
    }

    _terminalId.value = terminalId
    _startPolling(terminalId)

    return cmd
  }

  /**
   * Fecha um card do widget (sucesso ou erro).
   * Limpa o timer de auto-close se existir.
   */
  function dismissCommand(commandId) {
    if (_autoCloseTimers.has(commandId)) {
      clearTimeout(_autoCloseTimers.get(commandId))
      _autoCloseTimers.delete(commandId)
    }

    trackedCommands.value = trackedCommands.value.filter(c => c.id !== commandId)

    if (!trackedCommands.value.some(c => !c.is_processed)) {
      _stopPolling()
    }
  }

  /**
   * Reset total — chamar ao fazer logout ou trocar de terminal.
   */
  function reset() {
    _stopPolling()

    // Limpa todos os timers de auto-close
    _autoCloseTimers.forEach(timerId => clearTimeout(timerId))
    _autoCloseTimers.clear()

    trackedCommands.value  = []
    _submittingSlugs.value = []
    _terminalId.value      = null
  }

  // ---------------------------------------------------------------------------
  // Helpers privados
  // ---------------------------------------------------------------------------

  async function _fetchPending(terminalId) {
    try {
      const { data } = await api.get(`/terminals/${terminalId}/commands/pending`)
      return data.commands ?? []
    } catch {
      return []
    }
  }

  async function _fetchStatus(terminalId, commandId) {
    try {
      const { data } = await api.get(`/terminals/${terminalId}/commands/${commandId}`)
      return data.command ?? null
    } catch {
      return null
    }
  }

  function _startPolling(terminalId, intervalMs = 5000) {
    _stopPolling()
    _pollingTimer.value = setInterval(() => _poll(terminalId), intervalMs)
  }

  function _stopPolling() {
    if (_pollingTimer.value !== null) {
      clearInterval(_pollingTimer.value)
      _pollingTimer.value = null
    }
  }

  /**
   * Ciclo de polling:
   *  - Actualiza is_executing dos comandos ainda pendentes
   *  - Detecta comandos que saíram da fila e verifica o resultado final
   */
  async function _poll(terminalId) {
    const waitingIds = trackedCommands.value
      .filter(c => !c.is_processed)
      .map(c => c.id)

    if (waitingIds.length === 0) {
      _stopPolling()
      return
    }

    const pendingFromServer = await _fetchPending(terminalId)
    const pendingServerMap  = new Map(pendingFromServer.map(c => [c.id, c]))

    for (const id of waitingIds) {
      const serverCmd = pendingServerMap.get(id)

      if (serverCmd) {
        // Ainda pendente — sincroniza is_executing se mudou
        const idx = trackedCommands.value.findIndex(c => c.id === id)
        if (idx >= 0 && trackedCommands.value[idx].is_executing !== serverCmd.is_executing) {
          trackedCommands.value[idx] = {
            ...trackedCommands.value[idx],
            is_executing: serverCmd.is_executing,
          }
        }
        continue
      }

      // O comando saiu da fila — verifica resultado final
      const status = await _fetchStatus(terminalId, id)
      if (!status) continue

      const idx = trackedCommands.value.findIndex(c => c.id === id)
      if (idx < 0) continue

      if (status.is_processed && !status.has_error) {
        // Sucesso: mostra card verde com auto-close de 30s
        trackedCommands.value[idx] = {
          ...trackedCommands.value[idx],
          ...status,
          _succeeded: true,
        }
        const timerId = setTimeout(() => dismissCommand(id), SUCCESS_AUTO_CLOSE_MS)
        _autoCloseTimers.set(id, timerId)
      } else {
        // Erro: actualiza em-lugar, utilizador fecha manualmente
        trackedCommands.value[idx] = {
          ...trackedCommands.value[idx],
          ...status,
          _succeeded: false,
        }
      }
    }

    if (!trackedCommands.value.some(c => !c.is_processed)) {
      _stopPolling()
    }
  }

  // ---------------------------------------------------------------------------

  return {
    trackedCommands,
    activeCommands,
    hasPending,
    isCommandLocked,
    initialize,
    enqueue,
    dismissCommand,
    reset,
  }
})
